import { useState } from 'react';
import axios from 'axios';
import { UserPlus, Loader2, Copy } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const L = ({ label, children }) => <label className="block text-xs"><span className="uppercase text-yellow-100/50">{label}</span>{children}</label>;
const EMPTY = { customer_name: '', mobile: '', email: '', tag_type: 'bike', tag_ids: '', amount: 0, payment_mode: 'cash', note: '' };

export const ManualIssuePanel = ({ T, headers, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { ...f, amount: Number(f.amount) || 0, email: f.email.trim() || null, tag_ids: f.tag_ids.split(/[\s,]+/).filter(Boolean) };
      if (!body.tag_ids.length) delete body.tag_ids;
      const { data } = await axios.post(`${API}/admin/qr/manual-issue`, body, { headers });
      setRes(data); setF(EMPTY);
      toast({ title: `Issued ${data.tags.length} tag(s) · ${data.order_number}` });
      onDone?.();
    } catch (ex) { toast({ title: 'Issue failed', description: ex.response?.data?.detail || 'Try again' }); } finally { setBusy(false); }
  };

  const inp = `mt-1 w-full px-3 py-2 rounded-lg text-sm ${T.input}`;

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="qr-manual-issue">
      <form onSubmit={submit} className={`${T.card} p-6 space-y-3`}>
        <h3 className="font-semibold text-yellow-100 flex items-center gap-2"><UserPlus size={16} className="text-yellow-300" /> Issue tag manually</h3>
        <p className={`text-xs ${T.softText}`}>For offline / counter sales. Tag is assigned to the customer's mobile; a new account is created if none exists.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <L label="Customer name*"><input required value={f.customer_name} onChange={set('customer_name')} className={inp} data-testid="mi-name" /></L>
          <L label="Mobile*"><input required value={f.mobile} onChange={set('mobile')} placeholder="98xxxxxxxx" className={inp} data-testid="mi-mobile" /></L>
          <L label="Email (optional)"><input type="email" value={f.email} onChange={set('email')} className={inp} data-testid="mi-email" /></L>
          <L label="Tag type*"><select value={f.tag_type} onChange={set('tag_type')} className={inp} data-testid="mi-tag-type"><option value="bike">Bike (1 tag · 1 sticker)</option><option value="car">Car (1 tag · 2 stickers)</option><option value="combo">Combo (2 tags · 3 stickers)</option></select></L>
          <L label="Amount received (₹)"><input type="number" min={0} value={f.amount} onChange={set('amount')} className={inp} data-testid="mi-amount" /></L>
          <L label="Payment mode"><select value={f.payment_mode} onChange={set('payment_mode')} className={inp} data-testid="mi-payment-mode"><option value="cash">Cash</option><option value="upi">UPI</option><option value="free">Free / Complimentary</option></select></L>
        </div>
        <L label="Specific tag IDs (optional — else auto-picked)"><input value={f.tag_ids} onChange={set('tag_ids')} placeholder="TAG0012, TAG0013" className={`${inp} font-mono`} data-testid="mi-tag-ids" /></L>
        <L label="Note"><input value={f.note} onChange={set('note')} className={inp} data-testid="mi-note" /></L>
        <button disabled={busy} className={`${T.btnPrimary} text-sm disabled:opacity-60`} data-testid="mi-submit-btn">{busy ? <><Loader2 size={14} className="animate-spin" /> Issuing…</> : 'Issue tag'}</button>
      </form>
      {res && <IssueResult T={T} res={res} />}
    </div>
  );
};

const IssueResult = ({ T, res }) => {
  const copyAll = () => navigator.clipboard?.writeText(res.tags.map((t) => `${t.tag_id}: ${t.activation_code}`).join('\n'));
  return (
    <div className={`${T.card} p-6 space-y-3`} data-testid="mi-result">
      <h4 className="font-semibold text-yellow-100">Issued · <span className="font-mono">{res.order_number}</span></h4>
      <p className="text-xs text-yellow-300 font-semibold" data-testid="mi-stickers">Hand over {res.stickers} printed sticker(s)</p>
      <p className={`text-xs ${T.softText}`}>{res.customer.name} · {res.customer.phone}{res.customer.is_new ? ' · new account' : ' · existing account'}</p>
      {res.customer.temp_password && (
        <p className="text-xs text-yellow-200" data-testid="mi-temp-password">Login password: <span className="font-mono font-bold text-yellow-300">{res.customer.temp_password}</span> (share with customer)</p>
      )}
      <div className={`border ${T.divider} rounded-lg divide-y divide-yellow-500/15`}>
        {res.tags.map((t) => (
          <div key={t.tag_id} className="flex justify-between px-3 py-2 text-sm" data-testid={`mi-tag-${t.tag_id}`}>
            <span className="font-mono text-yellow-100">{t.tag_id}</span>
            <span className="font-mono font-bold text-yellow-300 tracking-widest" data-testid={`mi-code-${t.tag_id}`}>{t.activation_code}</span>
          </div>
        ))}
      </div>
      <button onClick={copyAll} className={`${T.btnOutline} text-xs`} data-testid="mi-copy-codes"><Copy size={12} /> Copy codes</button>
      <p className={`text-[11px] ${T.mutedText}`}>No SMS is sent — hand these codes to the customer. They also appear in the customer's My Orders.</p>
    </div>
  );
};
